import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { AlertModal } from '../components/AlertModal';
import { AlertFormData, User } from '../types/alert.types';
import { buildFinalVintedUrl } from '../hooks/useAlertForm';
import { API_BASE_URL } from '../api/apiClient';

export const CreateAlertPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  // Charger les informations utilisateur
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Erreur chargement utilisateur:', error);
      }
    };
    fetchUser();
  }, []);

  const handleSave = async (formData: AlertFormData) => {
    try {
      // URL finale : recherche rapide (searchText) ou URL collée (vintedUrl) + tous les filtres
      const vintedUrl = buildFinalVintedUrl(formData);

      // Transformer les données du format camelCase au format snake_case attendu par le backend
      const alertData = {
        name: formData.name,
        vinted_url: vintedUrl,
        category_id: formData.categoryId || null,
        category_path: formData.categoryPath || null,
        selected_conditions: formData.selectedConditions.length > 0 ? formData.selectedConditions : null,
        selected_sizes: formData.selectedSizes.length > 0 ? formData.selectedSizes.map(s => ({ id: s.id, title: s.title })) : null,
        selected_colors: formData.selectedColors.length > 0 ? formData.selectedColors : null,
        selected_brands: formData.selectedBrands.length > 0 ? formData.selectedBrands.map(b => ({ id: b.id, title: b.title })) : null,
        selected_statuses: (formData.selectedStatuses && formData.selectedStatuses.length > 0) ? formData.selectedStatuses : null,
        selected_materials: (formData.selectedMaterials && formData.selectedMaterials.length > 0) ? formData.selectedMaterials.map(m => ({ id: m.id, title: m.title })) : null,
        selected_patterns: (formData.selectedPatterns && formData.selectedPatterns.length > 0) ? formData.selectedPatterns.map(p => ({ id: p.id, title: p.title })) : null,
        selected_languages: (formData.selectedLanguages && formData.selectedLanguages.length > 0) ? formData.selectedLanguages.map(l => l.id) : null,
        selected_platforms: (formData.selectedPlatforms && formData.selectedPlatforms.length > 0) ? formData.selectedPlatforms.map(p => ({ id: p.id, title: p.title })) : null,
        selected_ratings: (formData.selectedRatings && formData.selectedRatings.length > 0) ? formData.selectedRatings.map(r => ({ id: r.id, title: r.title })) : null,
        min_price: formData.minPrice ? parseFloat(formData.minPrice) : null,
        max_price: formData.maxPrice ? parseFloat(formData.maxPrice) : null,
        min_likes: formData.minLikes ? parseInt(formData.minLikes) : null,
        max_likes: formData.maxLikes ? parseInt(formData.maxLikes) : null,
        max_pages: parseInt(formData.maxPages, 10) || 5,
        trending_only: formData.trendingOnly || false,
        must_include: formData.mustInclude.length > 0 ? formData.mustInclude : null,
        must_exclude: formData.mustExclude.length > 0 ? formData.mustExclude : null,
        time_value: 1,
        time_unit: 'jour'
      };

      console.log('Envoi des données:', alertData);

      const response = await fetch(`${API_BASE_URL}/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(alertData)
      });

      if (response.ok) {
        // Rediriger vers la liste des alertes
        navigate('/app');
      } else {
        const errorData = await response.json();
        console.error('Erreur création alerte:', errorData);
        
        // Gérer les erreurs de validation Pydantic (qui peuvent être des tableaux)
        let errorMessage = 'Erreur inconnue';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Erreurs de validation Pydantic : format [{loc: [...], msg: "...", type: "..."}, ...]
            errorMessage = errorData.detail
              .map((err: any) => {
                const field = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : err.loc;
                return `${field}: ${err.msg}`;
              })
              .join('\n');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        }
        
        window.alert(`Erreur lors de la création de l'alerte:\n${errorMessage}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur de connexion au serveur';
      window.alert(`Erreur lors de la création de l'alerte: ${errorMessage}`);
    }
  };

  const handleClose = () => {
    navigate('/app');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar gauche réutilisable */}
      <Sidebar
        user={user}
        currentPage="alerts"
        onLogout={handleLogout}
        onPageChange={(page) => {
          if (page === 'alerts') navigate('/app');
        }}
      />

      {/* Contenu principal - Prend toute la largeur disponible (sidebar 256px = ml-64) */}
      <main className="flex-1 ml-64 h-screen flex items-center justify-center bg-white p-0 overflow-hidden">
        <div className="w-full h-full flex items-center justify-center px-4 py-4 min-w-0">
          <div className="w-full h-full max-w-full">
            {/* Formulaire d'alerte en mode embedded (pleine page) */}
            <AlertModal
              show={true}
              editingAlert={null}
              onClose={handleClose}
              onSave={handleSave}
              embedMode={true}
            />
          </div>
        </div>
      </main>
    </div>
  );
};
